import { formatAddress, toArray } from '@footsteps/shared';
import { FlowStepId } from '../woz-flow.types';
import type {
  FlowAsyncHandler,
  FlowStepDefinition,
  FlowActionDefinition,
  FlowMultipleSelectOption,
  FlowStepRef,
  WozCaseViewModelRef,
} from '../woz-flow.types';
import type { WozCaseQuestion } from '../../woz-case.types';

/** Async step — fetches the question flow for the current complaint. */
export const getFlowStep: FlowStepDefinition = {
  id: FlowStepId.GetFlow,
  type: 'async',
  // handler is attached by WozFlow
  thinkingMessages: ['Ik ga de juiste vragen voor je ophalen...', 'Even geduld...'],
};

/** Shown when the get-flow API call fails. */
export const getFlowErrorStep: FlowStepDefinition = {
  id: FlowStepId.GetFlowError,
  type: 'message',
  text: 'Het ophalen van de vragenlijst is mislukt. Probeer het opnieuw.',
};

/**
 * Intro message shown before asking questions for a complaint.
 */
export function complaintIntroStep(
  complaintIndex: number,
  totalComplaints: number,
  complaintText: string,
  hasQuestions = true,
): FlowStepDefinition {
  let label: string;
  if (totalComplaints > 1) {
    label = `**Punt ${complaintIndex + 1}/${totalComplaints}:** ${complaintText}`;
  } else if (hasQuestions) {
    label = 'Ik heb een paar vragen over je bezwaar:';
  } else {
    label = 'Ik heb geen vragen over je bezwaar.';
  }

  return {
    id: FlowStepId.GetFlowIntro,
    type: 'message',
    text: label,
    meta: { complaintIndex },
  };
}

/** Generate an action step for a single-select question with pre-built actions. */
export function questionActionStep(
  complaintIndex: number,
  questionIndex: number,
  questionText: string,
  actions: FlowActionDefinition[]
): FlowStepDefinition {
  return {
    id: FlowStepId.GetFlowQuestion,
    type: 'action',
    text: questionText,
    actions,
    disableInput: true,
    waitForTrigger: FlowStepId.GetFlowQuestion,
    revertable: true,
    meta: { complaintIndex, questionIndex },
  };
}

/** Generate a multiple-select step for a question with checkbox options. */
export function questionMultipleSelectStep(
  complaintIndex: number,
  questionIndex: number,
  questionText: string,
  options: FlowMultipleSelectOption[]
): FlowStepDefinition {
  return {
    id: FlowStepId.GetFlowQuestion,
    type: 'multiple-select',
    text: questionText,
    multipleSelectOptions: options,
    disableInput: true,
    waitForTrigger: FlowStepId.GetFlowQuestion,
    revertable: true,
    meta: { complaintIndex, questionIndex },
  };
}

const ACTION_BUTTON_LABELS: Record<string, string> = {
  'select-address': 'Selecteer adres',
  'select-addresses': 'Selecteer adres(sen)',
};

/** Generate an action step for an "action" question type that triggers a modal. */
export function questionActionTypeStep(
  complaintIndex: number,
  questionIndex: number,
  questionText: string,
  actionPayload: string
): FlowStepDefinition {
  const label = ACTION_BUTTON_LABELS[actionPayload] ?? actionPayload;

  return {
    id: FlowStepId.GetFlowQuestion,
    type: 'action',
    text: questionText,
    actions: [
      { id: 'skip', label: 'Overslaan', event: 'trigger', payload: FlowStepId.GetFlowQuestion },
      { id: 'open-modal', label, event: 'openModal', payload: actionPayload, primaryButton: true },
    ],
    disableInput: true,
    waitForTrigger: FlowStepId.GetFlowQuestion,
    revertable: true,
    meta: {
      complaintIndex,
      questionIndex,
      action: actionPayload,
      onComplete: (viewModel: WozCaseViewModelRef, data: unknown) => {
        viewModel.setQuestionAnswer(complaintIndex, questionIndex, data as WozCaseQuestion['answer']);
        viewModel.completeTrigger(FlowStepId.GetFlowQuestion, { actionId: 'action-complete' });
      },
      formatBubble: (data: unknown) => {
        if (data == null) return null;
        const items = toArray(data as Record<string, unknown> | Record<string, unknown>[]);
        const lines: string[] = [];
        if (items.length > 1) {
          lines.push('**Geselecteerde adressen:**');
        } else {
          lines.push('**Geselecteerd adres:**');
        }
        lines.push(...items.map((answer) => `📍 ${formatAddress(answer as Record<string, unknown>)}`));
        return lines.join('\n');
      },
    },
  };
}

/** Generate an input step for an open-text question (enables chat input). */
export function questionOpenStep(
  complaintIndex: number,
  questionIndex: number,
  questionText: string
): FlowStepDefinition {
  return {
    id: FlowStepId.GetFlowQuestion,
    type: 'input',
    text: questionText,
    revertable: true,
    meta: { complaintIndex, questionIndex },
  };
}

/**
 * Create a get-flow async step for an additional round or a subsequent complaint.
 * The handler is injected because it lives in WozFlow (to avoid circular imports).
 */
export function createGetFlowRepeatStep(handler: FlowAsyncHandler): FlowStepDefinition {
  return {
    ...getFlowStep,
    handler,
  };
}

/** Rehydrate a get-flow step from a persisted FlowStepRef. */
export function rehydrateGetFlowStep(ref: FlowStepRef, handler: FlowAsyncHandler): FlowStepDefinition {
  return {
    ...getFlowStep,
    sequence: ref.sequence,
    meta: ref.meta,
    handler,
  };
}
